// @ts-check

import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useAppBridge } from "@shopify/app-bridge-react";
import { ActionList, Box, Button, ButtonGroup, Icon, IndexTable, InlineStack, Link, Popover, Spinner, Text } from "@shopify/polaris";
import { DataTableIcon, MenuHorizontalIcon, DeleteIcon, DuplicateIcon } from "@shopify/polaris-icons";

import { useGlobalContext } from "context/GlobalContext";
import { useUtmLinks } from "context/UtmLinksContext";

import { UTM_FIELDS } from "utils/utm-sets";
import { generateUtmLink } from "utils/index";

export const SavedUtmLinksTableRow = ({
  index,
  data,
  columns,
  sortedValues,
}) => {
  const shopify = useAppBridge();
  const navigate = useNavigate();
  const { refetchSavedUtmSets } = useGlobalContext();
  const {
    setError,
    setBaseUrl,
    setUtmSource,
    setUtmMedium,
    setUtmCampaign,
    setUtmContent,
    setUtmTerm,
  } = useUtmLinks();

  const [rowPopoverActive, setRowPopoverActive] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleRowPopoverActive = () => {
    setRowPopoverActive(active => !active);
  };

  const utmColumns = columns
    .filter((col) => UTM_FIELDS.map(({ key }) => key).includes(col))
    .slice()
    .sort((a, b) => sortedValues.indexOf(a) - sortedValues.indexOf(b))
    .map((col) => {
      let content = data[col] || "-";
      if (col === "utm_source" && data[col]) {
        const handleClick = () => {
          navigate(`/reports/source/${data[col]}`);
        }

        content = (
          <Link
            key={col}
            onClick={handleClick}
            removeUnderline
          >
            {data[col]}
          </Link>
        )
      }

      if (col === "utm_medium" && data[col]) {
        const handleClick = () => {
          navigate(`/reports/medium/${data[col]}`);
        }

        content = (
          <Link
            key={col}
            onClick={handleClick}
            removeUnderline
          >
            {data[col]}
          </Link>
        )
      }

      if (col === "utm_campaign" && data[col]) {
        const handleClick = () => {
          navigate(`/reports/campaign/${data[col]}`);
        }

        content = (
          <Link
            key={col}
            onClick={handleClick}
            removeUnderline
          >
            {data[col]}
          </Link>
        )
      }
      
      return (
        <IndexTable.Cell key={col}>{content}</IndexTable.Cell>
      )
    });

  const handleCopyFullUrl = () => {
    shopify.toast.show("Copied to Clipboard");
  };

  const handleViewUtmSetClick = () => {
    const params = {};

    for (const utmField of UTM_FIELDS) {
      params[utmField.key] = data[utmField.key] || "";
    }

    navigate(`/utm-sets/view?${new URLSearchParams(params).toString()}`);
  };

  const handleDuplicateClick = () => {
    if (data.domain) {
      setBaseUrl(data.domain);
    }
    if (data.utm_source) {
      setUtmSource(data.utm_source);
    }
    if (data.utm_medium) {
      setUtmMedium(data.utm_medium);
    }
    if (data.utm_campaign) {
      setUtmCampaign(data.utm_campaign);
    }
    if (data.utm_content) {
      setUtmContent(data.utm_content);
    }
    if (data.utm_term) {
      setUtmTerm(data.utm_term);
    }
  };

  const handleDeleteUtmSet = () => {
    setDeleting(true);
    setError("");

    return fetch(`/api/utm-sets/saved/${data.id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then(({ success }) => {
        if (!success) {
          throw new Error();
        }

        setRowPopoverActive(false);
        refetchSavedUtmSets();
      })
      .catch((error) => {
        setError("Error deleting saved UTM set");
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const utmLink = useMemo(() => {
    return generateUtmLink({
      baseUrl: data.domain,
      utmSource: data.utm_source,
      utmMedium: data.utm_medium,
      utmCampaign: data.utm_campaign,
      utmContent: data.utm_content,
      utmTerm: data.utm_term,
      type: "custom",
    });
  }, [data]);

  return (
    <IndexTable.Row id={data.id} position={index}>
      {utmColumns}
      {columns.includes("actions") ? (
        <IndexTable.Cell>
          <InlineStack align="end">
            <ButtonGroup variant="segmented">
              <CopyToClipboard text={utmLink} onCopy={handleCopyFullUrl}>
                <Button>
                  Copy UTM link
                </Button>
              </CopyToClipboard>
              <Popover
                active={rowPopoverActive}
                activator={
                  <Button
                    icon={MenuHorizontalIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRowPopoverActive()
                    }}
                  />
                }
                autofocusTarget="first-node"
                onClose={() => setRowPopoverActive(false)}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    {
                      content: (
                        <InlineStack gap="100" blockAlign="center">
                          <Box>
                            <Icon source={DataTableIcon} />
                          </Box>
                          <Text as="p">View analytics</Text>
                        </InlineStack>
                      ),
                      onAction: handleViewUtmSetClick,
                    },
                    {
                      content: (
                        <InlineStack gap="100" blockAlign="center">
                          <Box>
                            <Icon source={DuplicateIcon} />
                          </Box>
                          <Text as="p">Duplicate</Text>
                        </InlineStack>
                      ),
                      onAction: handleDuplicateClick,
                    },
                    {
                      content: deleting ? (
                        <Spinner size="small" />
                      ) : (
                        <InlineStack gap="100" blockAlign="center">
                          <Box>
                            <Icon source={DeleteIcon} tone="critical" />
                          </Box>
                          <Text as="p" tone="critical">Delete</Text>
                        </InlineStack>
                      ),
                      onAction: handleDeleteUtmSet,
                    },
                  ]}
                />
              </Popover>
            </ButtonGroup>
          </InlineStack>
        </IndexTable.Cell>
      ) : null}
    </IndexTable.Row>
  );
};
